/**
 * Native blog list — fetches articles from the backend proxy (/api/blog/posts)
 * and renders a compact, searchable card grid.
 * Uses IntersectionObserver to defer the fetch until the section scrolls into view.
 */
import { useState, useEffect, useRef } from "react";
import { Search, Loader2, Calendar, ArrowRight, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

function formatDate(raw) {
  if (!raw) return "";
  try {
    return new Date(raw).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return raw;
  }
}

/**
 * @param {Object} props
 * @param {number}  [props.limit]       — cap number of articles shown (omit = show all)
 * @param {boolean} [props.showSearch]  — show the search input (default true)
 * @param {string}  [props.className]
 */
export function SoroBlog({ limit, showSearch = true, className = "" }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    // Only fetch when the component enters the viewport (perf: avoids blocking LCP)
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        api.get("/blog/posts")
          .then((res) => setPosts(res.data.posts || []))
          .catch(() => setError(true))
          .finally(() => setLoading(false));
      },
      { rootMargin: "200px" }   // start 200px before it enters viewport
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const filtered = posts
    .filter((p) => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return p.title?.toLowerCase().includes(q) || p.excerpt?.toLowerCase().includes(q);
    })
    .slice(0, limit || 9999);

  if (loading) {
    return (
      <div ref={containerRef} className={`flex items-center justify-center py-16 ${className}`} data-testid="soro-blog-loading">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error || (!loading && posts.length === 0)) {
    return (
      <div ref={containerRef} className={`py-10 text-center text-muted-foreground text-sm ${className}`} data-testid="soro-blog-empty">
        <BookOpen className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
        <p>Blog posts coming soon. Check back later.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className} data-testid="soro-blog-widget">
      {showSearch && posts.length > 3 && (
        <div className="relative mb-6 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles…"
            className="pl-9 bg-[#050508]/50 border-white/10 focus-visible:ring-primary h-10 text-sm"
            data-testid="blog-search-input"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-center text-muted-foreground text-sm py-8" data-testid="blog-no-results">
          No articles match "<span className="text-white">{query}</span>"
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="blog-posts-grid">
          {filtered.map((post) => (
            <BlogCard key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
}

function BlogCard({ post }) {
  const url = `https://noxaiapp.com/blog/${post.slug || post.id}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-xl border border-white/10 bg-[#0F1015] hover:border-primary/30 hover:bg-[#12141c] transition-all duration-200 overflow-hidden"
      data-testid={`blog-post-${post.id}`}
    >
      {/* Thumbnail */}
      {post.image ? (
        <div className="aspect-video w-full overflow-hidden bg-[#050508]/50 shrink-0">
          <img
            src={post.image}
            alt={post.title || "Blog post"}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="aspect-video w-full bg-gradient-to-br from-[#12141c] to-[#080910] flex items-center justify-center shrink-0">
          <BookOpen className="h-8 w-8 text-primary/20" />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-heading text-sm font-black tracking-tight text-white leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {post.title || "Untitled"}
        </h3>
        {post.excerpt && (
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3 flex-1">
            {post.excerpt}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-1">
          {post.date && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60">
              <Calendar className="h-3 w-3" />
              {formatDate(post.isoDate || post.date)}
            </span>
          )}
          <span className="flex items-center gap-1 text-[11px] font-semibold text-primary/70 group-hover:text-primary transition-colors ml-auto">
            Read more <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </a>
  );
}
